import { useAuth0 } from "@auth0/auth0-react";
import {
	Alert,
	AlertIcon,
	Button,
	Checkbox,
	FormControl,
	FormErrorMessage,
	FormLabel,
	Input,
} from "@chakra-ui/react";
import { useMutation, useQuery } from "@apollo/client";
import { motion } from "framer-motion";
import { Check, ChevronsUpDown } from "lucide-react";
import { Helmet } from "react-helmet";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import {
	CommunityInviteRedirectStrategy,
	InviteJoinFlowType,
} from "src/__generated__/graphql";
import {
	getStoredCommunityInviteSession,
	storeCommunityInviteSession,
} from "src/features/communities/invite-session";
import type { StoredCommunityInviteSession } from "src/features/communities/invite-session.types";
import { Button as SelectorButton } from "src/components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "src/components/ui/command";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "src/components/ui/popover";
import { countriesData } from "src/constants";
import {
	COMMUNITY_INVITE_METADATA,
	INITIALISE_JOIN_BY_COMMUNITY_INVITE,
} from "src/features/communities/graphql";
import { cn } from "src/lib/utils";
import { cleanPhoneNumber, isValidIntlPhoneNumber } from "src/utlis/helpers";

const PRIMARY_BUTTON_PROPS = {
	borderRadius: "0",
	border: "1px solid #ED6E5C",
	bg: "#ED6E5C",
	color: "white",
	boxShadow: "none",
	_hover: { bg: "#D95D4C", borderColor: "#D95D4C" },
	_active: { bg: "#C95242", borderColor: "#C95242" },
};

const INPUT_PROPS = {
	borderRadius: "0",
	borderColor: "#D7D7D7",
	bg: "white",
	_focusVisible: {
		borderColor: "#ED6E5C",
		boxShadow: "none",
	},
};

const getPortalUrl = (inviteToken?: string) => {
	const baseUrl = `${window.location.origin}/community-invite`;

	if (!inviteToken) {
		return baseUrl;
	}

	return `${baseUrl}?token=${encodeURIComponent(inviteToken)}`;
};

const normalizeEmail = (value: string) => value.trim().toLowerCase();
const isCountryOption = (
	country: (typeof countriesData)[number]
): country is (typeof countriesData)[number] & {
	shortName: string;
	dialingCode: string;
} => Boolean(country.shortName && country.dialingCode);

const buildCommunityInvitePossessive = (
	communityName?: string | null
) => {
	if (!communityName?.trim()) {
		return "community's";
	}

	const trimmedName = communityName.trim();
	return /s$/i.test(trimmedName) ? `${trimmedName}'` : `${trimmedName}'s`;
};

export default function CommunityInvitePage() {
	const { loginWithRedirect } = useAuth0();
	const [searchParams] = useSearchParams();
	const storedSession = useMemo(() => getStoredCommunityInviteSession(), []);

	const inviteToken = useMemo(
		() => searchParams.get("token") || searchParams.get("inviteToken") || "",
		[searchParams]
	);

	const {
		data,
		loading: metadataLoading,
		error: metadataError,
		refetch,
	} = useQuery(COMMUNITY_INVITE_METADATA, {
		skip: !inviteToken,
		variables: { token: inviteToken },
		fetchPolicy: "network-only",
	});

	const [initialiseJoinByCommunityInvite, { loading: joinLoading, error: joinError }] =
		useMutation(INITIALISE_JOIN_BY_COMMUNITY_INVITE);

	const [name, setName] = useState("");
	const [email, setEmail] = useState("");
	const [phone, setPhone] = useState("");
	const [profession, setProfession] = useState("");
	const [openCountrySelector, setOpenCountrySelector] = useState(false);
	const [isAgreed, setIsAgreed] = useState(false);
	const [showNotAgreedError, setShowNotAgreedError] = useState(false);
	const [selectedCountry, setSelectedCountry] = useState<
		| ((typeof countriesData)[number] & {
				shortName: string;
				dialingCode: string;
		  })
		| null
	>(() => {
		const defaultCountry = countriesData
			.filter(isCountryOption)
			.find((country) => country.shortName === "NG");

		return defaultCountry || null;
	});
	const [fieldErrors, setFieldErrors] = useState<{
		email?: string;
		invite?: string;
		phone?: string;
	}>({});
	const [submissionError, setSubmissionError] = useState<string | null>(null);

	const cleanedPhone = cleanPhoneNumber(phone);
	const combinedPhoneNumber =
		cleanedPhone && selectedCountry
			? `+${selectedCountry.dialingCode}${cleanedPhone}`
			: "";
	const isValidPhoneNumber =
		!cleanedPhone ||
		(Boolean(combinedPhoneNumber) &&
			isValidIntlPhoneNumber(combinedPhoneNumber));

	useEffect(() => {
		if (!inviteToken || !storedSession) {
			return;
		}

		if (storedSession.inviteToken === inviteToken) {
			setEmail(storedSession.expectedEmail);
			setProfession(storedSession.professionPrefill || "");
		}
	}, [inviteToken, storedSession]);

	const organizationName = data?.communityInviteMetadata.organizationName;
	const communityInvitePossessive = buildCommunityInvitePossessive(organizationName);
	const inviteIsValid = data?.communityInviteMetadata.isValid ?? false;

	const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setSubmissionError(null);
		setShowNotAgreedError(false);

		const nextFieldErrors: typeof fieldErrors = {};
		if (!inviteToken) {
			nextFieldErrors.invite = "This invite link is missing its token.";
		}
		if (!email.trim()) {
			nextFieldErrors.email = "Your email is required to continue.";
		}
		if (phone.trim() && !isValidPhoneNumber) {
			nextFieldErrors.phone =
				"Enter a valid phone number for the selected country.";
		}

		setFieldErrors(nextFieldErrors);

		if (Object.keys(nextFieldErrors).length > 0) {
			return;
		}

		if (!isAgreed) {
			setShowNotAgreedError(true);
			return;
		}

		try {
			const response = await initialiseJoinByCommunityInvite({
				variables: {
					input: {
						inviteToken,
						name: name.trim(),
						email: normalizeEmail(email),
						phone: combinedPhoneNumber || undefined,
						profession: profession.trim() || undefined,
					},
				},
			});

			const startResult = response.data?.initialiseJoinByCommunityInvite;

			if (!startResult) {
				throw new Error("Invite join did not return a session token.");
			}

			if (
				startResult.redirectStrategy !== CommunityInviteRedirectStrategy.Auth0
			) {
				throw new Error(
					"This invite is not configured for the current sign-in flow."
				);
			}

			const session: StoredCommunityInviteSession = {
				inviteToken,
				inviteJoinSessionToken: startResult.inviteJoinSessionToken,
				organizationName: organizationName || "your community",
				flowType: startResult.flowType,
				redirectStrategy: startResult.redirectStrategy,
				expectedEmail: normalizeEmail(email),
				loginHint: startResult.loginHint || normalizeEmail(email),
				professionPrefill: profession.trim() || undefined,
				createdAt: new Date().toISOString(),
			};

			storeCommunityInviteSession(session);

			await loginWithRedirect({
				redirectUri: `${window.location.origin}/community-invite/callback`,
				login_hint: session.loginHint,
				...(startResult.flowType === InviteJoinFlowType.NewUser && {
					screen_hint: "signup",
				}),
			});
		} catch (error) {
			const message =
				error instanceof Error
					? error.message
					: "We could not start this invite right now.";
			setSubmissionError(message);
		}
	};

	const isLoading = metadataLoading || joinLoading;
	const inviteIsMissing = !inviteToken;
	const inviteHasError = Boolean(metadataError) || Boolean(joinError);

	return (
		<>
			<Helmet>
				<title>Community invite | LaborHack Pro</title>
			</Helmet>
			<div className="relative min-h-screen overflow-hidden bg-[#071116] text-slate-50">
				<div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(237,110,92,0.28),_transparent_34%),radial-gradient(circle_at_75%_20%,_rgba(255,255,255,0.14),_transparent_24%),linear-gradient(145deg,_#061018_0%,_#0a1a24_45%,_#04090d_100%)]" />
				<div className="absolute inset-0 opacity-[0.16] [background-image:linear-gradient(rgba(255,255,255,0.14)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.14)_1px,transparent_1px)] [background-size:84px_84px]" />
				<div className="relative mx-auto flex min-h-screen max-w-7xl flex-col lg:flex-row">
					<motion.section
						initial={{ opacity: 0, y: 16 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.45 }}
						className="flex flex-1 items-center px-6 py-16 sm:px-10 lg:px-16"
					>
						<div className="max-w-2xl space-y-8">
							<div className="inline-flex items-center gap-2 border border-white/15 bg-white/8 px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-slate-200">
								<span className="h-2 w-2 bg-brand-400" />
								Community invite
							</div>
							<div className="space-y-5">
								<h1 className="max-w-xl text-4xl font-semibold leading-tight text-white sm:text-5xl lg:text-6xl">
									Join this LaborHack community
								</h1>
								<p className="max-w-xl text-base leading-7 text-slate-300 sm:text-lg">
									Enter your details to accept {communityInvitePossessive}{" "}
									invite.
								</p>
							</div>

							<div className="border border-white/10 bg-[#0d1f2a]/80 p-5 text-sm text-slate-300 backdrop-blur">
								<p className="font-medium text-white">
									{organizationName ? (
										<>
											You&apos;re joining{" "}
											<span className="text-brand-300">{organizationName}</span>
											.
										</>
									) : (
										"Your invite will be checked before you continue."
									)}
								</p>
								<p className="mt-2 leading-6">
									Use the email address you want on your LaborHack account.
								</p>
							</div>
						</div>
					</motion.section>

					<motion.section
						initial={{ opacity: 0, y: 18 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, delay: 0.08 }}
						className="flex flex-1 items-center px-6 pb-16 sm:px-10 lg:px-16 lg:py-16"
					>
						<div className="w-full border border-white/12 bg-white/94">
							<div className="bg-[#f7f9fa] px-6 py-7 text-slate-900 sm:px-8 sm:py-8">
								<div className="mb-6 flex flex-col gap-4">
									<div>
										<div className="inline-flex border border-[#D7D7D7] bg-white px-3 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#4A4A4A]">
											{inviteIsValid ? "Invite verified" : "Invite pending"}
										</div>
										<h2 className="mt-4 font-[AeonikPro] text-[1.75rem] font-bold leading-tight text-[#121212] lg:text-[2rem]">
											Accept your community invite
										</h2>
										<p className="mt-2 text-sm leading-6 text-[#666666] lg:text-base lg:leading-7">
											We&apos;ll confirm your invite, then take you to sign in
											and continue.
										</p>
									</div>
								</div>

								{inviteIsMissing ? (
									<Alert
										status="warning"
										className="mb-5 border border-[#E7E0DA] bg-white"
										borderRadius="0"
									>
										<AlertIcon />
										This invite link is missing its token. Ask the community
										admin to resend the current invite URL.
									</Alert>
								) : null}

								{inviteHasError ? (
									<Alert
										status="error"
										className="mb-5 border border-[#E7E0DA] bg-white"
										borderRadius="0"
									>
										<AlertIcon />
										{metadataError?.message ||
											joinError?.message ||
											"We could not verify this invite right now."}
									</Alert>
								) : null}

								{submissionError ? (
									<Alert
										status="error"
										className="mb-5 border border-[#E7E0DA] bg-white"
										borderRadius="0"
									>
										<AlertIcon />
										{submissionError}
									</Alert>
								) : null}

								<form className="space-y-4" onSubmit={handleSubmit}>
									<FormControl>
										<FormLabel>Your full name</FormLabel>
										<Input
											value={name}
											onChange={(event) => setName(event.target.value)}
											placeholder="Ada Lovelace"
											{...INPUT_PROPS}
										/>
									</FormControl>

									<FormControl isInvalid={Boolean(fieldErrors.email)}>
										<FormLabel>Email address</FormLabel>
										<Input
											type="email"
											value={email}
											onChange={(event) => setEmail(event.target.value)}
											placeholder="ada@example.com"
											{...INPUT_PROPS}
										/>
										<FormErrorMessage>{fieldErrors.email}</FormErrorMessage>
									</FormControl>

									<div className="space-y-4">
										<FormControl isInvalid={Boolean(fieldErrors.phone)}>
											<FormLabel>Phone number</FormLabel>
											<div className="grid grid-cols-[112px_minmax(0,1fr)]">
												<Popover
													open={openCountrySelector}
													onOpenChange={setOpenCountrySelector}
												>
													<PopoverTrigger asChild>
														<SelectorButton
															variant="outline"
															role="combobox"
															aria-expanded={openCountrySelector}
															className="h-10 w-full justify-between rounded-none border border-[#D7D7D7] px-2 text-xs"
														>
															{selectedCountry
																? `${selectedCountry.shortName} (+${selectedCountry.dialingCode})`
																: "Country"}
															<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
														</SelectorButton>
													</PopoverTrigger>
													<PopoverContent className="w-[260px] p-0">
														<Command>
															<CommandInput placeholder="Choose country" />
															<CommandList>
																<CommandEmpty>No country found.</CommandEmpty>
																<CommandGroup>
																	{countriesData
																		.filter(isCountryOption)
																		.map((country) => (
																			<CommandItem
																				key={country.key}
																				value={country.nameAndDialingCode}
																				onSelect={() => {
																					setSelectedCountry(country);
																					setOpenCountrySelector(false);
																				}}
																			>
																				<Check
																					className={cn(
																						"mr-2 h-4 w-4",
																						selectedCountry?.dialingCode ===
																							country.dialingCode
																							? "opacity-100"
																							: "opacity-0"
																					)}
																				/>
																				{country.name} (+{country.dialingCode})
																			</CommandItem>
																		))}
																</CommandGroup>
															</CommandList>
														</Command>
													</PopoverContent>
												</Popover>
												<Input
													type="tel"
													value={phone}
													onChange={(event) => {
														setPhone(event.target.value);
														setFieldErrors((current) => ({
															...current,
															phone: undefined,
														}));
													}}
													placeholder="8034567890"
													className="!border-l-0"
													{...INPUT_PROPS}
												/>
											</div>
											<FormErrorMessage>{fieldErrors.phone}</FormErrorMessage>
										</FormControl>

										<FormControl>
											<FormLabel>Profession</FormLabel>
											<Input
												value={profession}
												onChange={(event) => setProfession(event.target.value)}
												placeholder="Tailor, welder, cleaner..."
												{...INPUT_PROPS}
											/>
										</FormControl>
									</div>

									<div className="border border-[#E7E7E7] bg-white px-4 py-4">
										<Checkbox
											colorScheme="brand"
											isChecked={isAgreed}
											onChange={(event) => {
												setIsAgreed(event.target.checked);
												if (event.target.checked) {
													setShowNotAgreedError(false);
												}
											}}
										>
											<p className="text-sm leading-6 text-[#545454]">
												I agree to LaborHack&apos;s{" "}
												<a
													href={`${process.env.REACT_APP_HOME_APP_URL}/terms`}
													target="_blank"
													rel="noreferrer"
													className="text-brand-600 underline underline-offset-4"
												>
													Terms
												</a>{" "}
												and{" "}
												<a
													href={`${process.env.REACT_APP_HOME_APP_URL}/privacy`}
													target="_blank"
													rel="noreferrer"
													className="text-brand-600 underline underline-offset-4"
												>
													Privacy policy
												</a>
												.
											</p>
										</Checkbox>
										{showNotAgreedError ? (
											<p className="mt-2 text-sm text-red-600">
												You must agree to the terms and conditions to continue.
											</p>
										) : null}
									</div>

									<div className="pt-2">
										<Button
											type="submit"
											isLoading={isLoading}
											loadingText="Loading..."
											size="lg"
											w="full"
											isDisabled={inviteIsMissing || !inviteIsValid}
											{...PRIMARY_BUTTON_PROPS}
											_disabled={{
												bg: "#F3B1A7",
												borderColor: "#F3B1A7",
												color: "white",
												cursor: "not-allowed",
											}}
										>
											Accept to join community
										</Button>
										{fieldErrors.invite ? (
											<p className="mt-3 text-sm text-red-600">
												{fieldErrors.invite}
											</p>
										) : null}
									</div>
								</form>

								<div className="mt-6 border-t border-slate-200 pt-5 text-sm text-slate-600">
									<p>
										Already started this invite?{" "}
										<RouterLink
											className="font-semibold text-brand-600 underline underline-offset-4"
											to={getPortalUrl(inviteToken)}
											onClick={() => {
												if (inviteToken) {
													refetch({ token: inviteToken });
												}
											}}
										>
											Reload invite status
										</RouterLink>
									</p>
								</div>
							</div>
						</div>
					</motion.section>
				</div>
			</div>
		</>
	);
}
